/**
 * Puts the CDN origin back on a client stack frame so Sentry can find the source map.
 *
 * **The mismatch this exists for.** The Falcon plugin uploads our maps under the artifact's full
 * parastorage URL:
 *
 *     https://static.parastorage.com/services/orion-web/<version>/_next/static/chunks/1010.13d.js
 *
 * while `@sentry/nextjs` normalises every browser frame down to `app:///…`, stripping BOTH the
 * origin and the asset-prefix path (`clientNormalizationIntegration.js`:
 * `filename.replace(origin, 'app://').replace(rewriteFramesAssetPrefixPath, '')`). Because our
 * `assetPrefix` IS the versioned CDN URL, the part it strips is exactly the part the artifact name
 * needs:
 *
 *     app:///_next/static/chunks/1010.13d.js
 *
 * Sentry looks that up as itself or `~/_next/…`, neither of which reaches an artifact filed under
 * `~/services/orion-web/<version>/_next/…`. So the maps sit next to the events and are never
 * opened — which is why, with the release tag landed (#10886), 194-file bundles uploaded and
 * correctly associated to the very release the events carry, a stack still reads
 * `_app-f822927f.js:214:255775`.
 *
 * Undoing the normalisation is the smallest thing that makes the two names identical.
 *
 * **This is temporary.** panorama#4321 moves the cloud upload to Debug IDs, which Sentry matches
 * with no URL and no release at all; its description names `@wix/orion-web` as a package that
 * keeps working. When that lands, delete this file, the `NEXT_PUBLIC_ORION_WEB_CDN_URL` entry in
 * next.config.js's `env`, and the call in instrumentation-client.ts. Nothing else depends on it.
 *
 * **Only `app:///_next/…` is touched, and that restriction is load-bearing.** The SDK's rewrite is
 * not limited to our own assets: its `else` branch rewrites EVERY frame whose filename parses as a
 * URL, so a third party's script arrives carrying the same prefix — `app:///scripts/inpage.js`
 * (MetaMask), `app:///executors/200.js` (Wix's platform executor), `app:///gtm.js`. Restoring the
 * CDN origin onto those would invent a parastorage path that never existed, point symbolication at
 * the wrong bundle, and — because a filename feeds the fingerprint — re-group every third-party
 * issue in the project. Our chunks all live under `_next/`, which is exactly the set the maps were
 * uploaded for, so that is the set this rewrites.
 *
 * No-ops without a CDN url (local and non-CI builds have none).
 */

/** The shape this needs out of a Sentry event. Structural so it stays testable on its own. */
export interface FrameBearingEvent {
  exception?: {
    values?: Array<{ stacktrace?: { frames?: Array<{ filename?: string }> } }>;
  };
}

const APP_PREFIX = 'app:///';
/** Only our own chunks. See the note above on why this is not merely `app:///`. */
const APP_NEXT_PREFIX = 'app:///_next/';

/**
 * Rewrites `app:///_next/…` frame filenames to `<cdnUrl>/_next/…`, in place. Mutating matches how
 * Sentry's own `rewriteFrames` iteratee works, and `beforeSend` hands us an event to edit.
 */
export function restoreCdnFrameUrls(event: FrameBearingEvent, cdnUrl: string): void {
  if (!cdnUrl) return;

  const base = cdnUrl.replace(/\/$/, '');

  for (const value of event.exception?.values ?? []) {
    for (const frame of value.stacktrace?.frames ?? []) {
      if (frame.filename?.startsWith(APP_NEXT_PREFIX)) {
        frame.filename = `${base}/${frame.filename.slice(APP_PREFIX.length)}`;
      }
    }
  }
}
